import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import _ from 'lodash';
import icon from 'media/images/svgSprite.svg';
import welcomeVideoMp4 from 'media/video/mody-welcome.mp4';
import welcomeVideoWebm from 'media/video/mody-welcome.webm';
import splashScreen from 'media/images/splash_screen.png';

import {
  WelcomeSection,
  ModalIconClose,
  VideoWrap,
  PlayIcon,
} from 'components/WelcomeVideo/WelcomeVideo.styled';

const STORAGE_KEY = 'welcome-video-current-time';
const THROTTLE_DELAY = 1000;

const WelcomeVideo = ({ onCloseWelcomeModal }) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const videoRef = useRef(null);

  const saveCurrentTime = useCallback(currentTime => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(currentTime));
    } catch (error) {
      console.error('Storage error:', error.message);
    }
  }, []);

  const throttledTimeUpdate = useMemo(
    () => _.throttle(saveCurrentTime, THROTTLE_DELAY),
    [saveCurrentTime]
  );

  const setLastSavedTime = useCallback(() => {
    const video = videoRef.current;

    try {
      const lastTime = localStorage.getItem(STORAGE_KEY);
      const fullTime = video.duration;
      let savedTime = null;

      if (lastTime && lastTime !== 'undefined' && lastTime !== 'null') {
        savedTime = JSON.parse(lastTime);

        if (savedTime !== fullTime) {
          video.currentTime = savedTime;
        }
      }
    } catch (error) {
      console.error('Error reading saved time:', error.message);
    }
  }, []);

  const handleVideoPlay = useCallback(() => {
    setIsVideoPlaying(true);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsVideoPlaying(false);
  }, []);

  const handleTimeUpdate = useCallback(
    e => {
      if (e.target?.currentTime) {
        throttledTimeUpdate(e.target.currentTime);
      }
    },
    [throttledTimeUpdate]
  );

  const handleLoadedData = useCallback(() => {
    if (videoRef.current) {
      setLastSavedTime(videoRef.current);
    }
  }, [setLastSavedTime]);

  const playVideo = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      try {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      } catch (error) {
        console.error('Error playing video:', error);
      }
    }
  }, []);

  const handleTouchEnd = e => {
    e.preventDefault();
    playVideo();
  };

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      video.addEventListener('loadeddata', setLastSavedTime);
      video.addEventListener('play', handleVideoPlay);
      video.addEventListener('pause', handleVideoPause);
      video.addEventListener('timeupdate', handleTimeUpdate);
    }

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('play', handleVideoPlay);
      video.removeEventListener('pause', handleVideoPause);
      video.removeEventListener('timeupdate', handleTimeUpdate);

      if (throttledTimeUpdate?.cancel) {
        throttledTimeUpdate.cancel();
      }
    };
  }, [
    handleLoadedData,
    handleTimeUpdate,
    handleVideoPause,
    handleVideoPlay,
    setLastSavedTime,
    throttledTimeUpdate,
  ]);

  return (
    <WelcomeSection>
      <ModalIconClose onClick={onCloseWelcomeModal}>
        <use href={`${icon}#icon-close`}></use>
      </ModalIconClose>
      <h3>Welcome video</h3>
      <VideoWrap>
        {!isVideoPlaying && (
          <PlayIcon
            onClick={playVideo}
            onTouchEnd={handleTouchEnd}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                playVideo();
              }
            }}
          >
            <use href={`${icon}#icon-play`}></use>
          </PlayIcon>
        )}

        <video
          ref={videoRef}
          id="welcome-video"
          controls
          aria-label="Welcome video"
          preload="metadata"
          poster={splashScreen}
          controlsList="nodownload "
          description="The video shows a brief instruction on how to use the service."
          onTouchEnd={handleTouchEnd}
          style={{
            WebkitTapHighlightColor: 'transparent',
          }}
          playsInline
        >
          <source src={welcomeVideoMp4} type="video/mp4" />
          <source src={welcomeVideoWebm} type="video/webm" />
          Your browser does not support the video tag.
        </video>
      </VideoWrap>
    </WelcomeSection>
  );
};

export default WelcomeVideo;
